import { toAppError, type AppError } from '@repo/api-client';
import { useSession } from '@repo/api-client/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { logger } from '@repo/logger';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { describeAppError } from '@/features/common/describeAppError';
import { AuthLayout } from './AuthLayout';

const signInSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Enter a valid email'),
  password: z.string().min(1, 'Password is required'),
});

type SignInValues = z.infer<typeof signInSchema>;

export function SignInScreen() {
  const { signIn } = useSession();
  const navigate = useNavigate();
  const [submitError, setSubmitError] = useState<AppError | null>(null);

  const form = useForm<SignInValues>({
    resolver: zodResolver(signInSchema),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setSubmitError(null);
    try {
      await signIn(values.email, values.password);
      navigate('/', { replace: true });
    } catch (err) {
      const appError = toAppError(err);
      logger.error('sign-in.failed', appError);
      setSubmitError(appError);
    }
  });

  const errorCopy = submitError ? describeAppError(submitError) : null;
  const wrongCreds = submitError?.kind === 'auth';

  return (
    <AuthLayout
      title="Welcome back"
      subtitle="Sign in to keep on top of every outstanding invoice."
      footer={
        <>
          New to Invoyss?{' '}
          <Link to="/sign-up" className="font-medium text-foreground underline-offset-4 hover:underline">
            Create an account
          </Link>
        </>
      }
    >
      {errorCopy ? (
        <Alert variant="danger" className="mb-4">
          <AlertTitle>{wrongCreds ? 'Email or password incorrect' : errorCopy.title}</AlertTitle>
          <AlertDescription>
            {wrongCreds
              ? 'Double-check your details and try again. If you forgot your password, use the link below.'
              : errorCopy.message}
          </AlertDescription>
        </Alert>
      ) : null}

      <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        <Field
          label="Email"
          htmlFor="email"
          required
          error={form.formState.errors.email?.message}
        >
          <Input
            type="email"
            autoComplete="email"
            placeholder="you@studio.com"
            invalid={!!form.formState.errors.email}
            {...form.register('email')}
          />
        </Field>

        <Field
          label="Password"
          htmlFor="password"
          required
          error={form.formState.errors.password?.message}
        >
          <Input
            type="password"
            autoComplete="current-password"
            invalid={!!form.formState.errors.password}
            {...form.register('password')}
          />
        </Field>

        <div className="-mt-1 flex justify-end">
          <Link
            to="/forgot-password"
            className="text-xs font-medium text-muted-foreground hover:text-foreground"
          >
            Forgot password?
          </Link>
        </div>

        <Button type="submit" size="lg" loading={form.formState.isSubmitting}>
          Sign in
        </Button>
      </form>
    </AuthLayout>
  );
}
